import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, CheckCircle2, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const FEATURES = [
  "Unbegrenzte Fahrzeuge & Inserate",
  "KI-Fotobooth: echte Studio-Bilder aus Smartphone-Fotos",
  "FIN-Scan & automatische Fahrzeugdaten",
  "KI-Inserate in Ihrer Händler-Tonalität",
  "Multi-Channel-Veröffentlichung & Exporte",
  "Content Studio für Social Media",
  "Händler-Branding (Logo, Farben, Wasserzeichen)",
];

export default function RegisterPage() {
  const [form, setForm] = useState({ autohaus_name: "", name: "", email: "", password: "", telefon: "" });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Willkommen bei CarOS! Ihr Autohaus ist startklar.");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err, "Registrierung fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="px-5 lg:px-8 h-16 flex items-center justify-between max-w-5xl mx-auto">
        <Link to="/" className="flex items-center gap-2" data-testid="register-logo-link">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
            <Car className="h-4 w-4 text-primary" />
          </div>
          <span className="font-display text-lg font-bold">CarOS</span>
        </Link>
        <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="register-login-link">
          Bereits Kunde? Anmelden
        </Link>
      </header>

      <div className="px-5 lg:px-8 py-8 lg:py-14 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-[-0.02em]" data-testid="register-headline">
            Pilotpartner werden
          </h1>
          <p className="text-sm lg:text-base text-muted-foreground mt-2 max-w-xl mx-auto">
            Bringen Sie Ihr nächstes Fahrzeug online, bevor es bei Ihnen auf dem Hof steht.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* Preis */}
          <Card className="border-primary/30 relative overflow-hidden" data-testid="register-pricing-card">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(400px 180px at 20% 0%, rgba(56,189,248,0.12), transparent 70%)" }} />
            <CardHeader className="relative pb-2">
              <span className="inline-flex w-fit rounded-full bg-primary/15 border border-primary/30 text-primary text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">Pilotpartner-Tarif</span>
              <div className="flex items-end gap-2 pt-3">
                <span className="font-display text-5xl font-bold tabular-nums" data-testid="register-price">199&nbsp;€</span>
                <span className="text-sm text-muted-foreground pb-2">/ Monat</span>
              </div>
              <p className="text-sm text-emerald-300 font-medium">Jederzeit kündbar – keine Mindestlaufzeit</p>
            </CardHeader>
            <CardContent className="relative space-y-2.5 pt-3">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {f}
                </div>
              ))}
              <div className="rounded-lg border border-border bg-secondary/30 p-3 mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                Keine Zahlungsdaten nötig – die Abrechnung startet erst nach dem gemeinsamen Onboarding mit Ihrem Ansprechpartner.
              </div>
            </CardContent>
          </Card>

          {/* Formular */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Registrierung</CardTitle>
              <p className="text-xs text-muted-foreground">Ein Zugang pro Autohaus – weitere Benutzer folgen später.</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3.5">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-autohaus">Name des Autohauses *</Label>
                  <Input id="reg-autohaus" required value={form.autohaus_name} onChange={set("autohaus_name")} placeholder="z.B. Autohaus Beispiel GmbH" className="h-11" data-testid="register-autohaus-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-name">Ihr Name *</Label>
                  <Input id="reg-name" required value={form.name} onChange={set("name")} placeholder="Vor- und Nachname" className="h-11" data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-email">E-Mail *</Label>
                  <Input id="reg-email" type="email" required value={form.email} onChange={set("email")} placeholder="ihr.name@autohaus.de" className="h-11" data-testid="register-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password">Passwort * (mind. 8 Zeichen)</Label>
                  <Input id="reg-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="h-11" data-testid="register-password-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-telefon">Telefon (optional)</Label>
                  <Input id="reg-telefon" value={form.telefon} onChange={set("telefon")} placeholder="für das Onboarding" className="h-11" data-testid="register-telefon-input" />
                </div>
                <Button type="submit" className="w-full h-12 gap-2 font-semibold" disabled={busy} data-testid="register-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  Jetzt Pilotpartner werden – 199 €/Monat
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">
                  Mit der Registrierung akzeptieren Sie die Pilotpartner-Konditionen: 199 €/Monat, jederzeit kündbar.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
